import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// ─── OAuth ───────────────────────────────────────────────
export const fetchTikTokOAuthUrl = createAsyncThunk(
  'tiktokAds/fetchOAuthUrl',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/tiktok/oauth/url');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to get OAuth URL');
    }
  }
);

export const fetchTikTokConnectionStatus = createAsyncThunk(
  'tiktokAds/fetchConnectionStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/tiktok/oauth/status');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch connection status');
    }
  }
);

export const disconnectTikTok = createAsyncThunk(
  'tiktokAds/disconnectTikTok',
  async (_, { rejectWithValue }) => {
    try {
      await api.delete('/marketing/tiktok/oauth/disconnect');
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to disconnect');
    }
  }
);

// ─── Ad Accounts ─────────────────────────────────────────
export const fetchTikTokAdAccounts = createAsyncThunk(
  'tiktokAds/fetchAdAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/tiktok/accounts');
      return response.data.accounts || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch ad accounts');
    }
  }
);

// ─── Campaigns ───────────────────────────────────────────
export const fetchTikTokCampaigns = createAsyncThunk(
  'tiktokAds/fetchCampaigns',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/tiktok/campaigns', { params });
      return response.data.campaigns || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch campaigns');
    }
  }
);

export const createTikTokCampaign = createAsyncThunk(
  'tiktokAds/createCampaign',
  async ({ advertiserId, campaignData }, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/tiktok/campaigns', {
        advertiserId,
        campaign: campaignData,
      });
      return response.data.campaign || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create campaign');
    }
  }
);

export const updateTikTokCampaign = createAsyncThunk(
  'tiktokAds/updateCampaign',
  async ({ campaignId, name, budget, status }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/marketing/tiktok/campaigns/${campaignId}`, {
        name,
        budget,
        status,
      });
      return response.data.campaign || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update campaign');
    }
  }
);

export const deleteTikTokCampaign = createAsyncThunk(
  'tiktokAds/deleteCampaign',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/marketing/tiktok/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete campaign');
    }
  }
);

export const syncTikTokCampaignMetrics = createAsyncThunk(
  'tiktokAds/syncMetrics',
  async ({ campaignId, advertiserId }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/marketing/tiktok/campaigns/${campaignId}/sync`,
        {},
        { params: { advertiserId } }
      );
      return response.data.campaign || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to sync metrics');
    }
  }
);

const initialState = {
  connected: false,
  businessAccountId: null,
  businessAccountName: null,
  adAccounts: [],
  campaigns: [],
  selectedAccount: null,

  loading: false,
  error: null,
};

const tiktokAdsSlice = createSlice({
  name: 'tiktokAds',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    selectAdAccount: (state, action) => {
      state.selectedAccount = action.payload;
    },
  },
  extraReducers: (builder) => {
    // OAuth URL
    builder
      .addCase(fetchTikTokOAuthUrl.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTikTokOAuthUrl.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.url) {
          window.location.href = action.payload.url;
        }
      })
      .addCase(fetchTikTokOAuthUrl.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Connection Status
    builder
      .addCase(fetchTikTokConnectionStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTikTokConnectionStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.connected = action.payload.connected;
        state.businessAccountId = action.payload.businessAccountId;
        state.businessAccountName = action.payload.businessAccountName;
        state.adAccounts = action.payload.adAccounts || [];
      })
      .addCase(fetchTikTokConnectionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.connected = false;
      });

    // Disconnect
    builder
      .addCase(disconnectTikTok.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(disconnectTikTok.fulfilled, (state) => {
        state.loading = false;
        state.connected = false;
        state.businessAccountId = null;
        state.businessAccountName = null;
        state.adAccounts = [];
        state.campaigns = [];
      })
      .addCase(disconnectTikTok.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Ad Accounts
    builder
      .addCase(fetchTikTokAdAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTikTokAdAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.adAccounts = action.payload;
      })
      .addCase(fetchTikTokAdAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Campaigns
    builder
      .addCase(fetchTikTokCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTikTokCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload;
      })
      .addCase(fetchTikTokCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create Campaign
    builder
      .addCase(createTikTokCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTikTokCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns.push(action.payload);
      })
      .addCase(createTikTokCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update Campaign
    builder
      .addCase(updateTikTokCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTikTokCampaign.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.campaigns.findIndex(
          (c) => c._id === action.payload._id
        );
        if (index !== -1) {
          state.campaigns[index] = action.payload;
        }
      })
      .addCase(updateTikTokCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete Campaign
    builder
      .addCase(deleteTikTokCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTikTokCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = state.campaigns.filter(
          (c) => c._id !== action.meta.arg.campaignId
        );
      })
      .addCase(deleteTikTokCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Sync Metrics
    builder
      .addCase(syncTikTokCampaignMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(syncTikTokCampaignMetrics.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.campaigns.findIndex(
          (c) => c._id === action.payload._id
        );
        if (index !== -1) {
          state.campaigns[index] = action.payload;
        }
      })
      .addCase(syncTikTokCampaignMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, selectAdAccount } = tiktokAdsSlice.actions;
export default tiktokAdsSlice.reducer;
